import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { MessagingButton } from "@/components/messaging/MessagingPanel";
import ChatbotWidget from "@/components/chatbot/ChatbotWidget";
import AuthCallback from "@/components/auth/AuthCallback";

// Pages
import HomePage from "@/pages/HomePage";
import EntreprisesPage from "@/pages/EntreprisesPage";
import RetraitesPage from "@/pages/RetraitesPage";
import CommentCaMarchePage from "@/pages/CommentCaMarchePage";
import ContactPage from "@/pages/ContactPage";
import MissionsPage from "@/pages/MissionsPage";
import PricingPage from "@/pages/PricingPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ExpertSearchPage from "@/pages/ExpertSearchPage";

// Dashboard Pages
import RetraiteDashboard from "@/pages/dashboard/RetraiteDashboard";
import EntrepriseDashboard from "@/pages/dashboard/EntrepriseDashboard";

// Messaging
import MessagingPage from "@/pages/MessagingPage";

// Admin
import AdminDashboard from "@/pages/admin/AdminDashboard";

// Blog
import BlogIndexPage from "@/pages/blog/BlogIndexPage";
import BlogArticlePage from "@/pages/blog/BlogArticlePage";

// Protected route component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('sfretire_token');
  if (!token) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// Dashboard redirect based on user type
const DashboardRedirect = () => {
  try {
    const user = JSON.parse(localStorage.getItem('sfretire_user') || 'null');
    if (user?.role === 'admin') {
      return <Navigate to="/admin" replace />;
    }
    if (user?.role === 'entreprise' || user?.user_type === 'entreprise') {
      return <Navigate to="/dashboard/entreprise" replace />;
    }
    return <Navigate to="/dashboard/retraite" replace />;
  } catch {
    return <Navigate to="/" replace />;
  }
};

// Router component to handle auth callback
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (from Google OAuth)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/entreprises" element={<EntreprisesPage />} />
        <Route path="/retraites" element={<RetraitesPage />} />
        <Route path="/comment-ca-marche" element={<CommentCaMarchePage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/missions" element={<MissionsPage />} />
        <Route path="/tarifs" element={<PricingPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/recherche-experts" element={<ExpertSearchPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        
        {/* Dashboard Routes */}
        <Route path="/dashboard" element={<ProtectedRoute><DashboardRedirect /></ProtectedRoute>} />
        <Route path="/dashboard/retraite" element={<ProtectedRoute><RetraiteDashboard /></ProtectedRoute>} />
        <Route path="/dashboard/entreprise" element={<ProtectedRoute><EntrepriseDashboard /></ProtectedRoute>} />
        
        {/* Messaging Routes */}
        <Route path="/messages" element={<ProtectedRoute><MessagingPage /></ProtectedRoute>} />
        <Route path="/messages/:conversationId" element={<ProtectedRoute><MessagingPage /></ProtectedRoute>} />
        
        {/* Admin Routes */}
        <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/dashboard/admin" element={<Navigate to="/admin" replace />} />
        
        {/* Blog Routes */}
        <Route path="/blog" element={<BlogIndexPage />} />
        <Route path="/blog/:slug" element={<BlogArticlePage />} />
        
        {/* Fallback routes */}
        <Route path="/a-propos" element={<HomePage />} />
        <Route path="/conditions" element={<HomePage />} />
        <Route path="/confidentialite" element={<HomePage />} />
        <Route path="/mentions-legales" element={<HomePage />} />
      </Routes>
      {/* Floating messaging button - visible when logged in */}
      <MessagingButton />
      {/* FAQ Chatbot - visible on all pages */}
      <ChatbotWidget />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </HelmetProvider>
  );
}

export default App;
